import { makeStyles, useMediaQuery } from '@material-ui/core'
import Actions from 'actions'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useHideHelpWidget from 'hooks/useHideHelpWidget'
import usePracticeTeamMembers from 'hooks/usePracticeTeamMembers'
import useRolePermissions from 'hooks/useRolePermissions'
import { isEmpty } from 'lodash'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { clearExpiredImageCache } from 'utils/imageProvider'
import { TAB_SESSION_ID } from 'utils/sessionUtils'
import ChatEmptyState from './Chat/ChatEmptyState/ChatEmptyState'
import PatientsChat from './Chat/PatientsChat'
import PatientsHeader from './PatientsHeader/PatientsHeader'
import PatientSwitchableView from './PatientSwitchableView/PatientSwitchableView'
import PatientViewResizer from './PatientViewResizer'
import ChatSkeletonV2 from './Timeline/V2/Skeletons/ChatSkeleton'
import TimelineV2 from './Timeline/V2/Timeline'

const PRESENCE_HEARTBEAT_MS = 30000
// How long after the user switches away (tab hidden) their presence expires for teammates.
const PRESENCE_AWAY_EXPIRE_MS = 60000

const useStyles = makeStyles({
  treatmentContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'white'
  },
  chatAndMilestonesDivider: {
    display: 'flex',
    flexDirection: 'row',
    // Take whatever the header leaves rather than subtracting the --patient-header-height constant:
    // the header may render taller than that when its content wraps, and a hardcoded offset would
    // put this area underneath it.
    flex: 1,
    minHeight: 0,
    position: 'relative',
    backgroundColor: 'var(--bg-color-timelineV2)'
  },
  shade: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    backgroundColor: '#333333',
    opacity: ({ isFilterOpen }) => (isFilterOpen ? '.2' : 0),
    zIndex: 50,
    transition: '.2s',
    pointerEvents: 'none'
  },
  emptyStateContainer: {
    width: '100%'
  }
})

const Patient = ({ isFilterOpen }) => {
  const dispatch = useDispatch()
  const patientSwitchableViewRef = useRef()
  const containerRef = useRef()

  useHideHelpWidget()
  usePracticeTeamMembers()

  const { permissions } = useRolePermissions()
  const doctor = useSelector(state => state.profileReducer.doctor)
  const presenceViewer = useMemo(
    () => ({ id: doctor?.user?.id, name: doctor?.name, photo: doctor?.photo, sessionId: TAB_SESSION_ID }),
    [doctor?.user?.id, doctor?.name, doctor?.photo]
  )

  const { grinDesignRefresh: grinDesignRefreshFF } = useFeatureFlags()

  const { patient, isLoadingPatient, requestedPatientId } = useSelector(state => state.patientsReducer)

  const isPatientNotFound = useMemo(() => requestedPatientId && isEmpty(patient), [patient, requestedPatientId])

  const isScreenBelowXXL = useMediaQuery('(max-width:2560px)') // Check if the viewport max-width is *up to* 2560px
  const defaultTimelineWidth = useMemo(() => (isScreenBelowXXL ? 55 : 50), [isScreenBelowXXL])
  const [timelineWidthPercent, setTimelineWidthPercent] = useState(defaultTimelineWidth)

  useEffect(() => {
    setTimelineWidthPercent(defaultTimelineWidth)
  }, [defaultTimelineWidth])

  useEffect(() => {
    const patientId = patient?.id
    if (patientId) {
      clearExpiredImageCache()

      dispatch(
        Actions.subscribeToContextNotifications({
          context: `patient/${patientId}`
        })
      )

      return () => {
        dispatch(
          Actions.unsubscribeFromContextNotifications({
            context: `patient/${patientId}`
          })
        )
        dispatch(Actions.clearCurrentPatient())
      }
    }
  }, [dispatch, patient?.id])

  // Broadcast HI presence so teammates can see who currently has this patient open.
  // While this is the active tab we send 'open' + a periodic heartbeat. When the user
  // switches to another tab (document.hidden) we stop heartbeating and, after a grace
  // period, broadcast 'close' so their presence expires for teammates — re-broadcasting
  // 'open' the moment they return to this tab.
  useEffect(() => {
    const patientId = patient?.id
    if (patientId && permissions.hiPresence && presenceViewer.id) {
      const report = action => dispatch(Actions.reportPatientPresence({ patientId, action, viewer: presenceViewer }))

      let heartbeat = null
      let awayTimer = null
      let isPresent = false // whether an 'open' is outstanding and still needs a matching 'close'
      const stopHeartbeat = () => {
        clearInterval(heartbeat)
        heartbeat = null
      }
      const sendClose = () => {
        if (isPresent) {
          report('close')
          isPresent = false
        }
      }
      const becomeActive = () => {
        clearTimeout(awayTimer)
        awayTimer = null
        stopHeartbeat()
        report('open')
        isPresent = true
        heartbeat = setInterval(() => report('heartbeat'), PRESENCE_HEARTBEAT_MS)
      }
      const becomeAway = () => {
        stopHeartbeat()
        clearTimeout(awayTimer)
        awayTimer = setTimeout(sendClose, PRESENCE_AWAY_EXPIRE_MS)
      }
      const onVisibilityChange = () => (document.hidden ? becomeAway() : becomeActive())

      if (document.hidden) {
        becomeAway()
      } else {
        becomeActive()
      }
      document.addEventListener('visibilitychange', onVisibilityChange)

      return () => {
        document.removeEventListener('visibilitychange', onVisibilityChange)
        stopHeartbeat()
        clearTimeout(awayTimer)
        sendClose()
      }
    }
  }, [dispatch, patient?.id, permissions.hiPresence, presenceViewer])

  const classes = useStyles({ isFilterOpen })
  return (
    <div className={classes.treatmentContainer}>
      <PatientsHeader />
      <div className={classes.chatAndMilestonesDivider} ref={containerRef}>
        {isPatientNotFound && !isLoadingPatient ? (
          <div className={classes.emptyStateContainer}>
            <ChatEmptyState showNoPatientSelected noPatientSelectedId="chat-empty-state-no-patient" />
          </div>
        ) : (
          <>
            <div style={{ width: `${timelineWidthPercent}%`, position: 'relative' }}>
              <TimelineV2 patientSwitchableViewRef={patientSwitchableViewRef} />
              {grinDesignRefreshFF && (
                <PatientViewResizer
                  containerRef={containerRef}
                  setWidthPercent={setTimelineWidthPercent}
                  defaultWidthPercent={defaultTimelineWidth}
                  resizerWidth={10}
                  minWidthPercent={40}
                  maxWidthPercent={60}
                />
              )}
            </div>
            <div style={{ width: `${100 - timelineWidthPercent}%` }} ref={patientSwitchableViewRef}>
              {isLoadingPatient ? (
                <ChatSkeletonV2 />
              ) : grinDesignRefreshFF ? (
                <PatientSwitchableView />
              ) : (
                <PatientsChat />
              )}
            </div>
          </>
        )}
        <div className={classes.shade} />
      </div>
    </div>
  )
}

export default React.memo(Patient)
